import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { HardDrive, Cloud, Clock, Play, CheckCircle, XCircle, AlertTriangle, Download, Trash2, RefreshCw, Database, Server } from 'lucide-react';

export function SystemBackupsPanel() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, historyRes] = await Promise.all([
        api.get('/api/admin/backups/status'),
        api.get('/api/admin/backups/history'),
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load backup data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const triggerBackup = async () => {
    if (running) return;
    setRunning(true);
    setError(null);
    try {
      await api.post('/api/admin/backups/run');
      await loadData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Backup failed');
    } finally {
      setRunning(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const StatusIcon = ({ s }) => {
    if (s === 'success') return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (s === 'failed') return <XCircle className="h-4 w-4 text-red-500" />;
    if (s === 'pending') return <Clock className="h-4 w-4 text-yellow-500 animate-spin" />;
    return <AlertTriangle className="h-4 w-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const runs = history?.runs || [];
  const driveFiles = history?.drive_files || [];
  const configured = status?.configured;

  return (
    <div className="space-y-6" data-testid="system-backups-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">System Backups</h2>
          <p className="text-sm text-muted-foreground">
            Automated nightly backups of MongoDB and SQL Server to Google Drive
          </p>
        </div>
        <button
          onClick={triggerBackup}
          disabled={running || !configured}
          data-testid="run-backup-btn"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
            ${running ? 'bg-gray-200 text-gray-500 cursor-wait' :
              !configured ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :
              'bg-blue-600 text-white hover:bg-blue-700'}`}
        >
          {running ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {running ? 'Running...' : 'Run Backup Now'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="backup-error">
          {error}
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-xl p-4 space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider">
            <Cloud className="h-3.5 w-3.5" /> Google Drive
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${configured ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <span className="text-sm font-medium">{configured ? 'Connected' : 'Not Configured'}</span>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider">
            <Clock className="h-3.5 w-3.5" /> Schedule
          </div>
          <p className="text-sm font-medium">{status?.schedule || 'Daily at 02:00 UTC'}</p>
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider">
            <Clock className="h-3.5 w-3.5" /> Next Backup
          </div>
          <p className="text-sm font-medium">{formatDate(status?.next_backup)}</p>
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider">
            <Trash2 className="h-3.5 w-3.5" /> Retention
          </div>
          <p className="text-sm font-medium">{status?.retention_days || 60} days</p>
        </div>
      </div>

      {!configured && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm" data-testid="backup-setup-notice">
          <p className="font-medium mb-2">Google Drive Setup Required</p>
          <p className="mb-2">To enable automated backups, a Google Service Account is needed:</p>
          <ol className="list-decimal list-inside space-y-1 text-xs">
            <li>Go to <a href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noreferrer" className="underline text-blue-600">Google Cloud Console &rarr; Service Accounts</a></li>
            <li>Create a Service Account (e.g. "eyvo-backups")</li>
            <li>Create a JSON key and download it</li>
            <li>Enable the <strong>Google Drive API</strong> in your project</li>
            <li>Share your Google Drive backup folder with the service account email</li>
            <li>Place the JSON key file on the server and set <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> in .env</li>
            <li>Set <code>GOOGLE_DRIVE_BACKUP_FOLDER_ID</code> to your folder ID</li>
          </ol>
        </div>
      )}

      {/* Backup Run History */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Recent Backup Runs</h3>
        {runs.length === 0 ? (
          <div className="bg-white border rounded-xl p-8 text-center text-muted-foreground text-sm">
            No backups have been run yet. {configured ? 'Click "Run Backup Now" to start.' : 'Configure Google Drive first.'}
          </div>
        ) : (
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Type</th>
                  <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">
                    <div className="flex items-center justify-center gap-1"><Database className="h-3.5 w-3.5" /> MongoDB</div>
                  </th>
                  <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">
                    <div className="flex items-center justify-center gap-1"><Server className="h-3.5 w-3.5" /> SQL Server</div>
                  </th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Duration</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run, i) => {
                  const started = run.started_at ? new Date(run.started_at) : null;
                  const completed = run.completed_at ? new Date(run.completed_at) : null;
                  const duration = started && completed ? Math.round((completed - started) / 1000) : null;
                  return (
                    <tr key={i} className="border-b last:border-0 hover:bg-slate-50/50">
                      <td className="px-4 py-2.5">{formatDate(run.started_at)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${run.manual ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                          {run.manual ? 'Manual' : 'Scheduled'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <StatusIcon s={run.mongo?.status} />
                          <span className="text-xs text-muted-foreground">{formatSize(run.mongo?.size_bytes)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <StatusIcon s={run.sql?.status} />
                          <span className="text-xs text-muted-foreground">{formatSize(run.sql?.size_bytes)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">
                        {duration ? `${duration}s` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Google Drive Files */}
      {driveFiles.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Backup Files on Google Drive</h3>
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Filename</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Type</th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Size</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Created</th>
                  <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Link</th>
                </tr>
              </thead>
              <tbody>
                {driveFiles.map((f, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-slate-50/50">
                    <td className="px-4 py-2.5 font-mono text-xs">{f.filename}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${f.type === 'MongoDB' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {f.type === 'MongoDB' ? <Database className="h-3 w-3" /> : <Server className="h-3 w-3" />}
                        {f.type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">{formatSize(f.size_bytes)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{formatDate(f.created_at)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {f.drive_link && (
                        <a href={f.drive_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1">
                          <Download className="h-3 w-3" /> Open
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemBackupsPanel;
